import clonedeep from 'lodash.clonedeep';
import { getDefaultData } from '../../lib/getDefaultValues';

const deepSetImmutable = (obj, path, value) => {
    if (!Array.isArray(path) || path.length === 0) return obj;
    const [head, ...rest] = path;
    const isArray = Array.isArray(obj);
    const container = isArray ? obj.slice() : { ...(obj || {}) };
    const key = head;
    if (rest.length === 0) {
        container[key] = value;
        return container;
    }
    const current = obj ? obj[key] : undefined;
    container[key] = deepSetImmutable(current, rest, value);
    return container;
};

const updateCollectionItemById = (collection = [], id, field, value) => {
  const idx = collection.findIndex((item) => item?.id === id || item?._id === id);
  if (idx === -1) return collection;

  const next = collection.slice();
  const currentItem = collection[idx];

  next[idx] = field.includes('.')
    ? deepSetImmutable(currentItem, field.split('.'), value)
    : { ...currentItem, [field]: value };

  return next;
};

const qrData = (dataObj, state) => {
    const { type, data, index, newBool } = dataObj;
    switch (type) {
        case 'bitsignal':
        case 'category':
        case 'description':
        case 'location':
        case 'location_snapshot':
        case 'logo_files':
        case 'menu':
        case 'microsite':
        case 'nickname':
        case 'notes':
        case 'qr_type':
        case 'tags':
            return { ...state, data: { ...state.data, [type]: data }};
        case 'redirect':
            return { ...state, data: { ...state.data, redirect_url: data }};
        case 'alt-redirect':
            let arr = state.data?.alternate_redirect_url ? [...state.data?.alternate_redirect_url] : [];

            if (newBool) {
                arr.push(data);
            };

            if (index === 0) {
                arr[0] = data;
            } else {
                if (!data && !newBool) {
                    arr.splice(index, 1);
                } else {
                    arr[index] = data;
                };
            };

            return { ...state, data: { ...state.data, alternate_redirect_url: arr || [] }};
        case 'tracker':
        case 'survey':
        case 'asset':
        case 'pdf': {
            let currentTypeData = state?.data?.[type] || {};

            const inferCollectionKey = () => {
                if (data?.collection) return data.collection;
                if (type === 'survey' || type === 'tracker') return 'fields';
                if (type === 'asset' || type === 'pdf') return 'files';
                return undefined;
            };

            const collectionKey = inferCollectionKey();

            if (collectionKey && (type === 'survey' || type === 'tracker')) {
                // normalize legacy keys (questions/columns) into fields for internal operations
                const legacyArray = currentTypeData.fields || currentTypeData.questions || currentTypeData.columns || [];
                currentTypeData = { ...currentTypeData, fields: legacyArray };
            }

            if (collectionKey && data && data.deleteId) {
                const existing = Array.isArray(currentTypeData[collectionKey]) ? currentTypeData[collectionKey] : [];
                const filtered = existing.filter(item => (item?.id ?? item?._id) !== data.deleteId);
                const nextTypeData = { ...currentTypeData, [collectionKey]: filtered };
                return { ...state, data: { ...state.data, [type]: nextTypeData } };
            }

            if (collectionKey && data && data.id && data.field && Object.prototype.hasOwnProperty.call(data, 'value')) {
                const baseCollection = (type === 'survey' || type === 'tracker') ? (currentTypeData.fields || currentTypeData.questions || currentTypeData.columns || []) : (currentTypeData[collectionKey] || []);
                const nextCollection = updateCollectionItemById(baseCollection, data.id, data.field, data.value);
                let nextTypeData;
                if (type === 'survey' || type === 'tracker') {
                    nextTypeData = { ...currentTypeData, fields: nextCollection };
                } else {
                    nextTypeData = { ...currentTypeData, [collectionKey]: nextCollection };
                }
                return { ...state, data: { ...state.data, [type]: nextTypeData } };
            }

            if (data && Array.isArray(data.path) && Object.prototype.hasOwnProperty.call(data, 'value')) {
                const nextTypeData = deepSetImmutable(currentTypeData, data.path, data.value);
                return { ...state, data: { ...state.data, [type]: nextTypeData } };
            }

            const obj = { ...state.data, [type]: { ...currentTypeData } };
            for (const [key, value] of Object.entries(data || {})) {
                // merge but if legacy keys provided (questions/columns) map them to fields
                if ((type === 'survey' || type === 'tracker') && (key === 'questions' || key === 'columns')) {
                    obj[type] = { ...obj[type], fields: value };
                } else {
                    obj[type] = { ...obj[type], [key]: value };
                }
            }

            // ensure fields key exists for survey/tracker even if empty
            if ((type === 'survey' || type === 'tracker')) {
                if (!obj[type].fields) {
                    obj[type].fields = obj[type].questions || obj[type].columns || [];
                }
                // strip legacy keys after ensuring fields is populated
                delete obj[type].questions;
                delete obj[type].columns;
            }
            return { ...state, data: obj };
        }
        case 'connect':
            // Deep merge connect updates to preserve all existing data
            const existingConnect = state.data?.connect || {};
            const updatedConnect = { ...existingConnect };
            const existingButtons = Array.isArray(existingConnect.buttons) ? existingConnect.buttons : [];
            let nextButtons = existingButtons;

            if (data?.buttonUpdate) {
                const { id, patch, removeKeys } = data.buttonUpdate || {};
                const idx = existingButtons.findIndex((button) => button?.id === id);
                if (idx !== -1) {
                    const updatedButton = { ...existingButtons[idx], ...(patch || {}) };
                    if (Array.isArray(removeKeys)) {
                        removeKeys.forEach((key) => {
                            delete updatedButton[key];
                        });
                    }
                    nextButtons = existingButtons.slice();
                    nextButtons[idx] = updatedButton;
                }
            }

            if (data?.buttonAdd) {
                nextButtons = [...nextButtons, data.buttonAdd];
            }

            if (data?.buttonDelete) {
                nextButtons = nextButtons.filter((button) => button?.id !== data.buttonDelete);
            }

            if (Array.isArray(data?.buttonsReorder) && data.buttonsReorder.length > 0) {
                const byId = new Map(nextButtons.map((button) => [button.id, button]));
                nextButtons = data.buttonsReorder
                    .map((id) => byId.get(id))
                    .filter(Boolean);
            }

            if (nextButtons !== existingButtons) {
                updatedConnect.buttons = nextButtons;
            }

            // Merge top-level properties
            for (const [key, value] of Object.entries(data || {})) {
                if (key === 'header' && typeof value === 'object') {
                    // Deep merge header to preserve image and other properties
                    updatedConnect.header = { ...existingConnect.header, ...value };
                } else if (!['buttonUpdate', 'buttonAdd', 'buttonDelete', 'buttonsReorder'].includes(key)) {
                    updatedConnect[key] = value;
                }
            }
            
            return { ...state, data: { ...state.data, connect: updatedConnect }};
        case 'connect-header':
            return { ...state, data: { ...state.data, connect: { ...state.data.connect, header: { ...state.data.connect.header, image: data || '' }}}}
        case 'schedule':
            const existingSchedule = state.data?.schedule || {};

            if (data?.delete) {
                const updatedSchedules = { ...existingSchedule };
                delete updatedSchedules[data.id];
                return { ...state, data: { ...state.data, schedule: updatedSchedules }};
            };

            const scheduleToUpdate = existingSchedule[data.id] || {};

            let updatedSchedule = { ...scheduleToUpdate };

            for (const [key, value] of Object.entries(data)) {
                updatedSchedule[key] = value
            };

            const finalSchedules = {
                ...existingSchedule,
                [data.id]: updatedSchedule
            };

            return { ...state, data: { ...state.data, schedule: finalSchedules }};
        case 'unlink':
            const newData = clonedeep(data.parent || {});
            newData.data.qr_style = data.qrStyle;
            return newData;
        case 'edit':
            const dataCopy = clonedeep(data || {});
            if (dataCopy?.data?.qr_style) {
                delete dataCopy.data.qr_style
            };
            // Preserve local description if incoming payload lacks it
            if (dataCopy?.data && typeof dataCopy.data.description === 'undefined' && state?.data?.description) {
                dataCopy.data.description = state.data.description;
            }
            return dataCopy;
        case 'new':
        case 'reset':
            return {
                id: data || '',
                data: getDefaultData(),
            };
        default:
            return data;
    };
};

export default qrData;
